import {
    createSlice, createAsyncThunk, isRejectedWithValue, isPending, PayloadAction
} from '@reduxjs/toolkit';
import {
    ApiViewEntityResponse, ApiViewResponseError, PaginationResponse,
    Direction, UserListItemModel, UserFields, UsersPagination
} from '../typings'
import { ProcessPagination } from '@lp/lp-ui';
import { getBodyErrors } from '../services/ApiService';
import identityService from '../services/IdentityService';
import { Message, MessageLevel } from '@lp/lp-ui';

interface UsersState {
    loading: boolean,
    data: PaginationResponse<UserListItemModel>,
    messages: Message[],
    pagination: UsersPagination,
    localPagination: UsersPagination,
    organizationId: number,
    selectedItems: number[],
    unSelectedItems: number[],
    selectAll: boolean,
    selectAllCount: number
}

const defaultPagination: UsersPagination = {
    sort: {
        sortBy: 'userName',
        sortDirection: Direction.Ascending
    },
    filters: [],
    page: {
        pageSize: 10,
        pageNumber: 1
    },
    filterString: '',
    loaded: false,
    shouldApplySearch: false,
    organizationId: 0
}

const initialState: UsersState = {
    loading: false,
    data: {
        pageNumber: 1,
        totalCount: 0,
        items: []
    },
    messages: [],
    pagination: defaultPagination,
    localPagination: defaultPagination,
    organizationId: 0,
    selectedItems: [],
    unSelectedItems: [],
    selectAll: false,
    selectAllCount: 0
}

const usersSlice = createSlice({
    name: 'users',
    initialState,
    reducers: {
        paginationSelected: (state, action: PayloadAction<UsersPagination>) => {
            const filtersChanged =
                JSON.stringify(state.pagination.filters) !== JSON.stringify(action.payload.filters) ||
                state.pagination.filterString !== action.payload.filterString;

            state.pagination = action.payload;
            state.localPagination = { ...state.localPagination, sort: action.payload.sort };

            if (filtersChanged) {
                state.selectedItems = [];
                state.unSelectedItems = [];
                state.selectAll = false;
                state.selectAllCount = 0;
            }
        },
        localPaginationSelected: (state, action: PayloadAction<UsersPagination>) => {
            state.localPagination = action.payload;
        },
        organizationSelected: (state, action: PayloadAction<number>) => {
            state.organizationId = action.payload;
            state.pagination = { ...state.pagination, organizationId: action.payload };
            state.localPagination = { ...state.localPagination, organizationId: action.payload };
            state.selectedItems = [];
            state.unSelectedItems = [];
            state.selectAll = false;
            state.selectAllCount = 0;
        },
        markOne: (state, action: PayloadAction<{ id: number, selected: boolean }>) => {
            if (action.payload.selected) {
                state.selectedItems.push(action.payload.id);
                if (state.selectAll) {
                    state.unSelectedItems = state.unSelectedItems.filter(x => x !== action.payload.id);
                }
            } else {
                state.selectedItems = state.selectedItems.filter(x => x !== action.payload.id);
                if (state.selectAll) {
                    state.unSelectedItems.push(action.payload.id);
                }
            }
        },
        markCurrentPage: (state, action: PayloadAction<{ ids: number[], selected: boolean }>) => {
            const { ids, selected } = action.payload;
            if (selected) {
                const newIds = ids.filter(id => !state.selectedItems.includes(id));
                state.selectedItems.push(...newIds);
                if (state.selectAll) {
                    state.unSelectedItems = state.unSelectedItems.filter(id => !ids.includes(id));
                }
            } else {
                state.selectedItems = state.selectedItems.filter(id => !ids.includes(id));
                if (state.selectAll) {
                    const newIds = ids.filter(id => !state.unSelectedItems.includes(id));
                    state.unSelectedItems.push(...newIds);
                }
            }
        },
        markAll: (state, action: PayloadAction<boolean>) => {
            state.selectAll = action.payload;
            state.selectAllCount = action.payload ? state.data.totalCount : 0;
            if (action.payload) {
                state.selectedItems = [...state.selectedItems, ...state.data.items
                    .filter(x => !state.selectedItems.includes(x.id))
                    .map(x => x.id)];
                state.unSelectedItems = [];
            } else {
                state.selectedItems = [];
            }
        },
        clearSelection: (state) => {
            state.selectedItems = [];
            state.unSelectedItems = [];
            state.selectAll = false;
            state.selectAllCount = 0;
        },
        usersDataReset: () => initialState,
    },
    extraReducers(builder) {
        builder
            .addCase(usersFilter.fulfilled, (state, action) => {
                state.loading = false;
                state.messages = [];
                state.data = action.payload.data;
                state.pagination = { ...state.pagination, shouldApplySearch: false, loaded: true };
            })
            .addMatcher(isApiPending, (state) => {
                state.loading = true;
            })
            .addMatcher(isApiRejected, (state, action) => {
                state.loading = false;
                if (action.payload) {
                    state.messages = action.payload.map(e => ({ details: e.detail, level: MessageLevel.Error }));
                }
            })
    }
});

export const usersFilter = createAsyncThunk<ApiViewEntityResponse<PaginationResponse<UserListItemModel>>, UsersPagination, { rejectValue: ApiViewResponseError[] }>
    ('users/filter', async (pagination: UsersPagination, { rejectWithValue }) => {
        try {
            const processed = ProcessPagination(pagination, UserFields);
            const response = await identityService.filterUsers(pagination.organizationId, processed);
            return response;
        } catch (err: any) {
            return rejectWithValue(await getBodyErrors(err));
        }
    });

const isApiPending = isPending(usersFilter);
const isApiRejected = isRejectedWithValue(usersFilter);

export const {
    paginationSelected,
    localPaginationSelected,
    organizationSelected,
    markOne,
    markAll,
    markCurrentPage,
    clearSelection,
    usersDataReset
} = usersSlice.actions

export default usersSlice.reducer
